// @flow
import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { forEach, groupBy } from 'lodash';
import cx from 'classnames';

import Checkbox from 'components/Checkbox';
import { DisabledTooltipLayer } from './SelectFromList';
import EventTracking from 'lib/EventTracking';
import GroupAvatar from 'groups/components/GroupAvatar';
import MediaObject from 'components/MediaObject';
import TargetFilterMenu from 'chat/components/TargetFilterMenu';
import Text from 'components/Text';
import { selectRecipient } from 'reducers/roleBasedMessagingSearch';
import { ComposerContext } from './ComposerContext';
import type { UIEventContext } from './ComposerContext';

type Props = {
  recipient: GroupRecipient,
  recipientIsSelected: boolean,
  disabledCopy: ?string,

  // Redux dispatchers
  onSelectRecipient: (recipient: GroupRecipient, filter: ?MessageFilterType) => *,
};

class GroupRecipientItem extends React.Component<Props> {
  logSelectionEvent = (uiEventContext: UIEventContext) => {
    if (this.props.recipientIsSelected) {
      EventTracking.trackKey('announcements.share_to.group.unselect.click', uiEventContext);
      return;
    }
    EventTracking.trackKey('announcements.share_to.group.select.click', uiEventContext);
  };

  handleTopLevelSelection = () => {
    this.props.onSelectRecipient(this.props.recipient, null);
  };

  renderGroupRecipient = () => {
    const { recipient, recipientIsSelected } = this.props;
    const { group } = recipient;
    const filterArray = [];
    const groupedFilters = groupBy(recipient.filters, 'type');
    forEach(groupedFilters, (values, type) => {
      const filter = { type, values: values.map(value => value.value) };
      filterArray.push(
        <TargetFilterMenu
          key={filter.type}
          filter={filter}
          recipient={recipient}
          recipientIsSelected={recipientIsSelected}
          dataTestSelector={`split-composer-group-filter-${group.id}`}
        />
      );
    });
    return (
      <MediaObject gutter="small" center>
        <GroupAvatar group={group} size="small" />
        <div>
          <Text size="small" element="div" truncate>
            {group.className}
          </Text>
          {filterArray.length > 0 && filterArray}
        </div>
      </MediaObject>
    );
  };

  render() {
    const { recipient, disabledCopy, recipientIsSelected } = this.props;
    const { group, id } = recipient;

    const component = (
      <ComposerContext.Consumer>
        {composerContext => (
          <li
            className={cx('RecipientItem', {
              'RecipientItem--disabled': !!disabledCopy,
            })}
            key={id}
            onClick={() => {
              this.handleTopLevelSelection();
              this.logSelectionEvent(composerContext.getUIEventContext());
            }}
            data-test-selector={`split-composer-group-${group.id}`}>
            <MediaObject figure="right" center>
              {this.renderGroupRecipient()}
              <Checkbox onChange={this.handleTopLevelSelection} checked={recipientIsSelected} />
            </MediaObject>
          </li>
        )}
      </ComposerContext.Consumer>
    );

    if (disabledCopy) {
      return <DisabledTooltipLayer text={disabledCopy}>{component}</DisabledTooltipLayer>;
    }

    return component;
  }
}

const mapDispatchToProps = (dispatch: Function) => {
  return bindActionCreators(
    {
      onSelectRecipient: selectRecipient,
    },
    dispatch
  );
};

export { GroupRecipientItem };

export default connect(undefined, mapDispatchToProps)(GroupRecipientItem);
